import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase, handleSupabaseError, insertWithUserId } from "@/lib/hooks/api/utils";
import { notifyTimeEntryCreated } from "@/lib/tasks/slack-webhook";
import { useBustDashboardCache } from "@/lib/hooks/api/dashboard/use-dashboard-summary";
import { Time_Entries, Time_EntriesInsertInput, Time_EntriesUpdateInput } from "@/src/gql/graphql";
import { Time_EntriesInsertInputSchema, Time_EntriesUpdateInputSchema } from "@/src/gql/validation";

export function useCreateTimeEntry() {
  const queryClient = useQueryClient();
  const bustDashboard = useBustDashboardCache();

  return useMutation({
    mutationFn: async (timeEntryData: Time_EntriesInsertInput) => {
      const validatedData = Time_EntriesInsertInputSchema.parse(timeEntryData);
      // `time_entries.date_worked` and `.description` are NOT NULL with no
      // default, but the GraphQL insert-input type marks every field optional, so
      // an omitted value used to travel all the way to PostgREST and come back as
      // a constraint violation. Fail here, where the message is actionable.
      const { date_worked, description } = validatedData;
      if (!date_worked) {
        throw new Error("A time entry needs a date worked.");
      }
      if (typeof description !== "string") {
        throw new Error("A time entry needs a description.");
      }
      // The GraphQL insert-input type allows an explicit `null` for
      // database-defaulted columns; PostgREST would try to write that NULL.
      // `id` / `created_at` / `updated_at` are always database-generated here.
      const { id: _id, created_at: _createdAt, updated_at: _updatedAt, ...insertable } =
        validatedData;
      const cleanedData = {
        ...insertable,
        date_worked,
        description,
        ticket_id:
          typeof validatedData.ticket_id === "string" && validatedData.ticket_id.trim() !== ""
            ? validatedData.ticket_id.trim()
            : null,
        hours: Number(validatedData.hours),
        billable_rate: validatedData.billable_rate ? Number(validatedData.billable_rate) : null,
      };

      const { data, error } = await insertWithUserId("time_entries", cleanedData);

      if (error) {
        console.error("Error creating time entry:", error);
        handleSupabaseError(error, "create time entry");
      }

      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["time-entries"] });
      void bustDashboard(); // logged time changes the dashboard time summary
      toast.success("Time entry created successfully");
      void notifyTimeEntryCreated(data);
    },
    retry: 0,
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      toast.error(errorMessage);
      console.error("Error creating time entry:", error);
    },
  });
}

export function useUpdateTimeEntry() {
  const queryClient = useQueryClient();
  const bustDashboard = useBustDashboardCache();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Time_EntriesUpdateInput }) => {
      const validatedData = Time_EntriesUpdateInputSchema.parse(updates);

      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("time_entries")
        .update(validatedData)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("Error updating time entry:", error);
        throw new Error(error.message);
      }

      return data as Time_Entries;
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: ["time-entries"] });
      await queryClient.cancelQueries({ queryKey: ["time-entry", id] });
      await queryClient.cancelQueries({ queryKey: ["time-entries-paginated"] });

      const previousTimeEntries = queryClient.getQueryData<Time_Entries[]>(["time-entries"]);
      const previousTimeEntry = queryClient.getQueryData<Time_Entries>(["time-entry", id]);
      const previousPaginated = queryClient.getQueryData<{ data: Time_Entries[] }>([
        "time-entries-paginated",
      ]);

      queryClient.setQueryData(["time-entry", id], (old: Time_Entries | undefined) => {
        if (!old) return old;
        return { ...old, ...updates };
      });

      queryClient.setQueryData(["time-entries"], (old: Time_Entries[] | undefined) => {
        if (!old) return old;
        return old.map((entry) => (entry.id === id ? { ...entry, ...updates } : entry));
      });

      queryClient.setQueryData(
        ["time-entries-paginated"],
        (old: { data: Time_Entries[] } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data?.map((entry: Time_Entries) =>
              entry.id === id ? { ...entry, ...updates } : entry
            ),
          };
        }
      );

      return { previousTimeEntries, previousTimeEntry, previousPaginated };
    },
    onSuccess: (data: Time_Entries) => {
      queryClient.setQueryData(["time-entry", data.id], data);

      queryClient.setQueryData(["time-entries"], (old: Time_Entries[] | undefined) => {
        if (!old) return old;
        return old.map((entry) => (entry.id === data.id ? data : entry));
      });

      queryClient.setQueryData(
        ["time-entries-paginated"],
        (old: { data: Time_Entries[] } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data?.map((entry: Time_Entries) => (entry.id === data.id ? data : entry)),
          };
        }
      );

      void bustDashboard(); // hours/rate edits change the dashboard time summary
      toast.success("Time entry updated successfully");
    },
    onError: (
      error,
      _variables,
      context:
        | {
            previousTimeEntries?: Time_Entries[];
            previousTimeEntry?: Time_Entries;
            previousPaginated?: { data: Time_Entries[] };
          }
        | undefined
    ) => {
      if (context?.previousTimeEntries) {
        queryClient.setQueryData(["time-entries"], context.previousTimeEntries);
      }
      if (context?.previousTimeEntry) {
        queryClient.setQueryData(["time-entry", _variables.id], context.previousTimeEntry);
      }
      if (context?.previousPaginated) {
        queryClient.setQueryData(["time-entries-paginated"], context.previousPaginated);
      }

      toast.error("Failed to update time entry");
      console.error("Error updating time entry:", error);
    },
  });
}

export function useDeleteTimeEntry() {
  const queryClient = useQueryClient();
  const bustDashboard = useBustDashboardCache();

  return useMutation({
    mutationFn: async (id: string) => {
      const supabase = getSupabase();
      const { error } = await supabase.from("time_entries").delete().eq("id", id);

      if (error) {
        console.error("Error deleting time entry:", error);
        throw new Error(error.message);
      }

      return { success: true };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["time-entries"] });
      void bustDashboard(); // removing time changes the dashboard time summary
      toast.success("Time entry deleted successfully");
    },
    onError: (error) => {
      toast.error("Failed to delete time entry");
      console.error("Error deleting time entry:", error);
    },
  });
}
